import React from "react";
import { Target, Video, Clock, BarChart3, Users, FileText, GraduationCap, HeartPulse, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CareerCard } from "./CareerCard";
import { GOAL_CATEGORY_LABELS, OWNER_LABELS, scoreBarColor } from "@/lib/careerConfig";
import { formatDate, cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const CATEGORY_ICONS = {
  visibility: Video, playing_time: Clock, performance: BarChart3, transfer: Users,
  admin: FileText, contract: FileText, education: GraduationCap, health: HeartPulse,
  selection: Trophy, ranking: Trophy,
};

export function ObjectivesOverview({ goals, onOpenAll }) {
  const active = goals.filter((g) => g.status === "active")
    .sort((a, b) => (b.progress || 0) - (a.progress || 0))
    .slice(0, 6);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <CareerCard icon={Target} title={tfr("Objectifs en cours")}
      subtitle={`${goals.filter((g) => g.status === "active").length} objectif(s) actif(s)`}
      testid="objectives-overview-card"
      right={
        <Button variant="outline" size="sm" onClick={onOpenAll} className="h-8 text-xs" data-testid="objectives-view-all">
          {tfr("Voir tous")}
        </Button>
      }>
      {active.length === 0 ? (
        <div className="text-center py-8" data-testid="objectives-empty">
          <p className="text-sm text-muted-foreground">{tfr("Aucun objectif actif.")}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={onOpenAll}>{tfr("Définir des objectifs")}</Button>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {active.map((g) => {
            const Icon = CATEGORY_ICONS[g.category] || Target;
            const overdue = g.due_date && g.due_date < today;
            return (
              <li key={g.id}>
                <button onClick={onOpenAll} data-testid={`objective-row-${g.id}`}
                  className="w-full grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-1 py-3 text-left hover:bg-secondary/30 rounded-lg px-2 -mx-2 transition-colors">
                  <span className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{g.title}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{GOAL_CATEGORY_LABELS[g.category] || "Autre"}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-2.5 min-w-0">
                    <span className="text-xs font-semibold tabular-nums w-9">{g.progress || 0}%</span>
                    <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
                      <div className={cn("h-full rounded-full", scoreBarColor(g.progress || 0))} style={{ width: `${g.progress || 0}%` }} />
                    </div>
                  </div>
                  <span className={cn("hidden sm:block text-[11px] tabular-nums whitespace-nowrap", overdue ? "text-danger font-medium" : "text-muted-foreground")}>
                    {g.due_date ? formatDate(g.due_date) : "—"}
                  </span>
                  <span className="hidden sm:block text-[11px] text-muted-foreground w-14 text-right">{OWNER_LABELS[g.owner_type] || "Agent"}</span>
                  <div className="col-span-3 sm:hidden flex items-center gap-2.5 w-full">
                    <span className="text-xs font-semibold tabular-nums w-9">{g.progress || 0}%</span>
                    <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
                      <div className={cn("h-full rounded-full", scoreBarColor(g.progress || 0))} style={{ width: `${g.progress || 0}%` }} />
                    </div>
                    <span className={cn("text-[11px]", overdue ? "text-danger" : "text-muted-foreground")}>
                      {g.due_date ? formatDate(g.due_date) : ""}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </CareerCard>
  );
}
